import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Alert,
  Slider,
  Paper,
  IconButton
} from '@mui/material';
import {
  Extension as PuzzleIcon,
  CheckCircle as CheckIcon,
  Refresh as RefreshIcon,
  WarningAmber as WarningIcon,
  PlayArrow as PlayIcon
} from '@mui/icons-material';

interface PuzzleVerificationDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title?: string;
  warningText?: string;
  semester?: number;
  academicYear?: number;
  loading?: boolean;
}

export const PuzzleVerificationDialog: React.FC<PuzzleVerificationDialogProps> = ({
  open,
  onClose,
  onConfirm,
  title = 'ยืนยันการจัดสรรที่ฝึกประสบการณ์อัตโนมัติ',
  warningText = 'ระบบจะทำการประมวลผลจัดสรรนักศึกษา อาจารย์นิเทศก์ และโรงเรียนตามเกณฑ์อัตโนมัติ กรุณาเลื่อนชิ้นส่วนจิ๊กซอว์เพื่อยืนยันความตั้งใจในการดำเนินการ',
  semester = 1,
  academicYear = 2569,
  loading = false
}) => {
  // Puzzle dimensions
  const containerWidth = 360;
  const containerHeight = 160;
  const pieceSize = 44;
  const maxSliderValue = containerWidth - pieceSize;

  // Target randomized position (between 35% and 85% of width)
  const [targetX, setTargetX] = useState(220);
  const [sliderValue, setSliderValue] = useState(0);
  const [isVerified, setIsVerified] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [feedbackMsg, setFeedbackMsg] = useState('');

  // Generate new target on open
  const resetPuzzle = () => {
    const minX = Math.floor(containerWidth * 0.4);
    const maxX = Math.floor(containerWidth * 0.82);
    const newTarget = Math.floor(Math.random() * (maxX - minX + 1)) + minX;
    setTargetX(newTarget);
    setSliderValue(0);
    setIsVerified(false);
    setFeedbackMsg('');
  };

  useEffect(() => {
    if (open) {
      resetPuzzle();
    }
  }, [open]);

  const handleSliderChange = (event: Event, newValue: number | number[]) => {
    if (isVerified) return;
    const val = typeof newValue === 'number' ? newValue : newValue[0];
    setSliderValue(val);
    setIsDragging(true);

    // Tolerance window ±7px
    const diff = Math.abs(val - targetX);
    if (diff <= 7) {
      setIsVerified(true);
      setSliderValue(targetX); // Snap exactly to target
      setFeedbackMsg('✓ ยืนยันสำเร็จ ปลดล็อกระบบจัดสรรแล้ว');
    }
  };

  const handleSliderCommit = () => {
    setIsDragging(false);
    if (!isVerified) {
      // Return to start if not verified
      setSliderValue(0);
      setFeedbackMsg('⚠️ กรุณาลากชิ้นส่วนให้ตรงกับช่องว่างพอดี');
    }
  };

  return (
    <Dialog open={open} onClose={loading ? undefined : onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'secondary.main', fontWeight: 'bold' }}>
        <PuzzleIcon color="secondary" />
        {title}
      </DialogTitle>

      <DialogContent dividers>
        <Alert severity="warning" icon={<WarningIcon />} sx={{ mb: 2 }}>
          <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
            ภาคเรียนที่ {semester} ปีการศึกษา {academicYear}
          </Typography>
          <Typography variant="caption" sx={{ display: 'block', mt: 0.5 }}>
            {warningText}
          </Typography>
        </Alert>

        <Box sx={{ textAlign: 'center', mb: 1 }}>
          <Typography variant="caption" color="textSecondary" sx={{ fontWeight: 'bold' }}>
            🔒 กลไกความปลอดภัย: เลื่อนสไลเดอร์เพื่อประกบชิ้นส่วนจิ๊กซอว์ให้ลงช่อง
          </Typography>
        </Box>

        {/* Puzzle Visual Area */}
        <Box
          sx={{
            width: containerWidth,
            height: containerHeight,
            mx: 'auto',
            position: 'relative',
            borderRadius: 2,
            overflow: 'hidden',
            boxShadow: 'inset 0 0 10px rgba(0,0,0,0.15)',
            background: isVerified
              ? 'linear-gradient(135deg, #10b981 0%, #059669 100%)'
              : 'linear-gradient(135deg, #1e3a8a 0%, #3b82f6 50%, #06b6d4 100%)',
            border: isVerified ? '2px solid #10b981' : '2px solid #3b82f6',
            transition: 'background 0.4s ease, border 0.4s ease',
            userSelect: 'none'
          }}
        >
          {/* Background Decorative Illustration */}
          <Box sx={{ position: 'absolute', inset: 0, p: 2, color: 'white', opacity: isVerified ? 0.9 : 0.85, pointerEvents: 'none' }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 'bold', textShadow: '0 1px 3px rgba(0,0,0,0.5)' }}>
              คณะครุศาสตร์ มรภ.กำแพงเพชร
            </Typography>
            <Typography variant="caption" sx={{ display: 'block', opacity: 0.9, textShadow: '0 1px 2px rgba(0,0,0,0.5)' }}>
              ระบบ TPMS - จัดสรรการฝึกสอน
            </Typography>
            <Typography variant="caption" sx={{ display: 'block', mt: 1, fontSize: '0.65rem', opacity: 0.75 }}>
              Teaching Practicum Auto-Placement Challenge
            </Typography>
          </Box>

          {/* Reset button inside top-right */}
          <IconButton
            size="small"
            onClick={resetPuzzle}
            disabled={isVerified || loading}
            sx={{ position: 'absolute', top: 6, right: 6, color: 'white', bgcolor: 'rgba(0,0,0,0.25)', '&:hover': { bgcolor: 'rgba(0,0,0,0.45)' } }}
            title="สุ่มตำแหน่งใหม่"
          >
            <RefreshIcon fontSize="small" />
          </IconButton>

          {/* Target Slot (The missing cut-out area) */}
          <Box
            sx={{
              position: 'absolute',
              left: targetX,
              top: (containerHeight - pieceSize) / 2,
              width: pieceSize,
              height: pieceSize,
              borderRadius: 1.5,
              border: isVerified ? '2px solid #ffffff' : '2px dashed rgba(255,255,255,0.85)',
              bgcolor: isVerified ? 'rgba(255,255,255,0.3)' : 'rgba(0,0,0,0.45)',
              boxShadow: isVerified ? '0 0 15px #ffffff' : 'inset 0 0 8px rgba(0,0,0,0.6)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              transition: 'all 0.3s ease'
            }}
          >
            {isVerified && <CheckIcon sx={{ color: 'white', fontSize: 26 }} />}
          </Box>

          {/* Draggable Puzzle Piece */}
          <Box
            sx={{
              position: 'absolute',
              left: sliderValue,
              top: (containerHeight - pieceSize) / 2,
              width: pieceSize,
              height: pieceSize,
              borderRadius: 1.5,
              bgcolor: isVerified ? '#10b981' : '#f59e0b',
              border: '2px solid #ffffff',
              boxShadow: isVerified ? '0 0 12px #10b981' : '0 4px 12px rgba(0,0,0,0.45)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: isVerified ? 'default' : 'grab',
              transform: isDragging ? 'scale(1.06)' : 'scale(1)',
              transition: isDragging ? 'none' : 'left 0.3s ease, transform 0.2s ease',
              zIndex: 10
            }}
          >
            {isVerified ? (
              <CheckIcon sx={{ color: 'white', fontSize: 28 }} />
            ) : (
              <PuzzleIcon sx={{ color: 'white', fontSize: 26 }} />
            )}
          </Box>
        </Box>

        {/* Interactive Slider Controller */}
        <Paper elevation={0} sx={{ mt: 2.5, p: 1.5, bgcolor: '#f8fafc', borderRadius: 2, border: '1px solid #e2e8f0' }}>
          <Box sx={{ px: 1 }}>
            <Slider
              value={sliderValue}
              min={0}
              max={maxSliderValue}
              onChange={handleSliderChange}
              onChangeCommitted={handleSliderCommit}
              disabled={isVerified || loading}
              sx={{
                color: isVerified ? '#10b981' : '#3b82f6',
                height: 10,
                '& .MuiSlider-thumb': {
                  width: 28,
                  height: 28,
                  bgcolor: isVerified ? '#10b981' : '#3b82f6',
                  border: '2px solid white',
                  boxShadow: '0 2px 6px rgba(0,0,0,0.3)',
                  '&:hover, &.Mui-focusVisible': {
                    boxShadow: '0 0 0 8px rgba(59, 130, 246, 0.16)'
                  }
                },
                '& .MuiSlider-track': {
                  border: 'none',
                  bgcolor: isVerified ? '#10b981' : '#3b82f6'
                },
                '& .MuiSlider-rail': {
                  opacity: 0.35,
                  bgcolor: '#94a3b8'
                }
              }}
            />
          </Box>
          <Typography
            variant="caption"
            sx={{
              display: 'block',
              textAlign: 'center',
              fontWeight: 'bold',
              color: isVerified ? '#059669' : feedbackMsg.startsWith('⚠️') ? '#d97706' : '#64748b',
              mt: 0.5
            }}
          >
            {isVerified
              ? '✓ ยืนยันสำเร็จ ปลดล็อกเรียบร้อยแล้ว!'
              : feedbackMsg || '👈 เลื่อนแถบสไลเดอร์ไปทางขวาเพื่อประกบชิ้นส่วนจิ๊กซอว์ 👉'}
          </Typography>
        </Paper>
      </DialogContent>

      <DialogActions sx={{ p: 2, justifyContent: 'space-between' }}>
        <Button onClick={onClose} disabled={loading} color="inherit">
          ยกเลิก
        </Button>
        <Button
          onClick={onConfirm}
          disabled={!isVerified || loading}
          variant="contained"
          color="secondary"
          startIcon={isVerified ? <PlayIcon /> : <PuzzleIcon />}
          sx={{
            px: 3,
            fontWeight: 'bold',
            boxShadow: isVerified ? '0 4px 14px rgba(220, 0, 78, 0.4)' : 'none'
          }}
        >
          {loading ? 'กำลังประมวลผล...' : 'เริ่มกระบวนการจัดสรรอัตโนมัติ'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PuzzleVerificationDialog;